import { useQuery } from "@apollo/client";
import type { ClientReviewsQuery } from "@/__generated__/graphql";
import {
  ClientReviewsDocument,
  type ClientReviewsInput,
} from "@/__generated__/graphql";
import type { ClientReviewData } from "@/types/review";
import {
  PackageDepartmentEnum,
  PackageServiceTypeEnum,
} from "@/__generated__/graphql";
import {
  departmentMapping,
  PackageServiceTypeMapping,
} from "@/lib/service/helpers";

const getServiceBookedName = (
  department: PackageDepartmentEnum,
  serviceType: PackageServiceTypeEnum,
) => {
  if (!department || !serviceType) return "";

  let name = departmentMapping[department];

  if (
    department === PackageDepartmentEnum.DeepCleaning &&
    serviceType === PackageServiceTypeEnum.DeepCleaning
  ) {
    name = PackageServiceTypeMapping[PackageServiceTypeEnum.DeepCleaning];
  }

  return name;
};

const getClientReviewData = (
  review: ClientReviewsQuery["clientReviews"]["clientReviews"][0],
): ClientReviewData => {
  return {
    id: review.id,
    clientName: review.clientName ?? "",
    ltv: review.clientLifetimeBooking ?? 0,
    rating: review.rating ?? 0,
    date: review.serviceDate ? new Date(review.serviceDate) : new Date(),
    createdAt: review.serviceDate ? new Date(review.serviceDate).getTime() : 0,
    service: getServiceBookedName(
      review.department ?? PackageDepartmentEnum.HomeCleaning,
      review.serviceType?.[0] as PackageServiceTypeEnum,
    ),
    description: review.ratingComment ?? "",
    images:
      review.ratingAttachment?.map((attachment) => attachment.downloadUrl) ||
      [],
  };
};

export const useClientReviewsQuery = (filters: ClientReviewsInput) => {
  const result = useQuery(ClientReviewsDocument, {
    variables: {
      filters,
    },
  });

  const list =
    result.data?.clientReviews?.clientReviews.map(getClientReviewData) ?? [];
  const total = result.data?.clientReviews?.count ?? 0;
  const averageRating = result.data?.clientReviews?.averageRating ?? 0;

  return { ...result, list, total, averageRating };
};
